// ─────────────────────────────────────────────────────────────────────────────
// Motion, the scroll-storytelling primitives for the marketing pages.
//
// <Reveal>   brings content in on the single easing curve the whole site uses,
//            cubic-bezier(0.22, 1, 0.36, 1), over 700ms: opacity 0 to 1, a 10px
//            blur clearing to 0, and a 12px rise. The blur is the part that makes
//            it read as one deliberate motion rather than a generic fade.
//            `delay` staggers siblings. preset="shot" keeps the older tilted
//            screenshot landing for pages that still ask for it.
// <CountUp>  animates the leading number of a stat ("9+", "100%", "$48,290")
//            from 0 when it enters view; prefix/suffix ride along untouched.
//
// Both collapse to static rendering under prefers-reduced-motion.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from 'react';

const reduceMotion = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// One curve for every transition on the marketing site.
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

const HIDDEN = {
    rise: 'translateY(12px)',
    fade: 'none',
    shot: 'perspective(1400px) rotateX(14deg) translateY(64px) scale(0.965)',
};

// Only the standard reveal carries the blur; a tilted screenshot landing would
// smear under it.
const BLUR = { rise: 'blur(10px)', fade: 'blur(10px)', shot: 'blur(0px)' };

export function Reveal({ children, preset = 'rise', delay = 0, className = '', style }) {
    const ref = useRef(null);
    const [on, setOn] = useState(reduceMotion);

    useEffect(() => {
        if (on) return undefined;
        const el = ref.current;
        if (!el || typeof IntersectionObserver !== 'function') { setOn(true); return undefined; }
        const io = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setOn(true); io.disconnect(); } },
            { threshold: 0.12, rootMargin: '0px 0px -48px 0px' },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [on]);

    const dur = preset === 'shot' ? 1.15 : 0.7;
    return (
        <div
            ref={ref}
            className={className}
            style={{
                ...style,
                opacity: on ? 1 : 0,
                filter: on ? 'blur(0px)' : (BLUR[preset] || BLUR.rise),
                transform: on ? (preset === 'shot' ? 'perspective(1400px) rotateX(0deg) translateY(0) scale(1)' : 'none') : HIDDEN[preset] || HIDDEN.rise,
                transition: `opacity ${dur}s ${EASE} ${delay}ms, filter ${dur}s ${EASE} ${delay}ms, transform ${dur}s ${EASE} ${delay}ms`,
                willChange: on ? 'auto' : 'opacity, filter, transform',
            }}
        >
            {children}
        </div>
    );
}

// Splits "$48,290", "9+", "100%", "6 apps" into prefix / number / suffix and
// animates the number from 0 once visible. Falls back to static text when the
// value has no digits or motion is reduced.
export function CountUp({ value, duration = 1300, className = '' }) {
    const ref = useRef(null);
    const match = String(value).match(/^([^0-9]*)([\d,]+(?:\.\d+)?)(.*)$/);
    const target = match ? parseFloat(match[2].replace(/,/g, '')) : null;
    const decimals = match && match[2].includes('.') ? match[2].split('.')[1].length : 0;
    const grouped = match ? match[2].includes(',') : false;
    const [display, setDisplay] = useState(() => (reduceMotion() || target == null ? null : 0));

    useEffect(() => {
        if (display === null || target == null) return undefined;
        const el = ref.current;
        if (!el || typeof IntersectionObserver !== 'function') { setDisplay(target); return undefined; }
        let raf = 0;
        const io = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            io.disconnect();
            const start = performance.now();
            const tick = (now) => {
                const p = Math.min(1, (now - start) / duration);
                const eased = 1 - Math.pow(1 - p, 3);
                setDisplay(target * eased);
                if (p < 1) raf = requestAnimationFrame(tick);
            };
            raf = requestAnimationFrame(tick);
        }, { threshold: 0.4 });
        io.observe(el);
        return () => { io.disconnect(); cancelAnimationFrame(raf); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, duration]);

    if (target == null || display === null) return <span className={className}>{value}</span>;
    const num = display.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: grouped });
    return <span ref={ref} className={className}>{match[1]}{num}{match[3]}</span>;
}
